const { isSafari, isEdge } = require('react-device-detect');
const { localAsset } = require('@Helpers/asset');
const documentHead = require('@Helpers/documentHead').default;

function setFavicon(faviconHref) {
  document.querySelector('link[rel="icon"]').href = faviconHref;
}

function setBrowserTabLoading() {
  if (isSafari || isEdge) {
    if (document.title) {
      if (document.title.substr(0, 2) === '! ') {
        document.title = `... ${document.title.substr(2)}`;
      } else if (document.title.substr(0, 4) !== '... ') {
        document.title = `... ${document.title}`;
      }
    } else {
      document.title = `... ${documentHead}`;
    }
  } else {
    setFavicon(localAsset('favicon/loading.png'));

    if (document.title) {
      if (document.title.substr(0, 4) === '... ') {
        document.title = document.title.substr(4);
      } else if (document.title.substr(0, 2) === '! ') {
        document.title = document.title.substr(2);
      }
    } else {
      document.title = documentHead;
    }
  }
}

function setBrowserTabReady() {
  if (!isSafari || isEdge) {
    setFavicon(localAsset('favicon/ready.png'));
  }

  if (document.title) {
    if (document.title.substr(0, 4) === '... ') {
      document.title = `! ${document.title.substr(4)}`;
    } else if (document.title.substr(0, 2) !== '! ') {
      document.title = `! ${document.title}`;
    }
  } else {
    document.title = `! ${documentHead}`;
  }
}

function setBrowserTabDefault() {
  if (!isSafari || isEdge) {
    setFavicon('/favicon.png');
  }

  if (document.title) {
    if (document.title.substr(0, 4) === '... ') {
      document.title = document.title.substr(4);
    } else if (document.title.substr(0, 2) === '! ') {
      document.title = document.title.substr(2);
    }
  } else {
    document.title = documentHead;
  }
}

export function setFaviconStatus(faviconStatus) {
  switch (faviconStatus) {
    case 'loading':
      setBrowserTabLoading();
      break;

    case 'ready':
      setBrowserTabReady();
      break;

    default:
      setBrowserTabDefault();
      break;
  }
}

const { format } = require('date-fns');
const dateFnsTz = require('date-fns-tz');

const formatDate = dateFnsTz.format || (dateFnsTz.default && dateFnsTz.default.format);
const toZonedTime = dateFnsTz.toZonedTime || (dateFnsTz.default && dateFnsTz.default.toZonedTime);

function getTimeFormat(dateTime, shorten) {
  const time = dateTime.split('T')[1];

  if (shorten) {
    return `M/dd/yy${time ? ' h:mmaaa' : ''}`;
  }

  return `MMM dd, yyyy${time ? ' hh:mmaaa' : ''}`;
}

// Add timezone abbreviation in parentheses, e.g. (PDT)
function getUserTimezoneAbbr(date = new Date()) {
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZoneName: 'short',
  }).formatToParts(date);
  const abbr = parts.find((p) => p.type === 'timeZoneName');
  return abbr ? `(${abbr.value})` : '';
}

export function dateToISOString(date, tz) {
  try {
    let jsDate;
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      jsDate = date;
    } else if (date && typeof date.getTime === 'function') {
      const ms = date.getTime();
      if (!Number.isNaN(ms)) {
        jsDate = new Date(ms);
      } else {
        throw new Error('Invalid date: getTime() is NaN');
      }
    } else {
      jsDate = new Date(date);
    }
    if (!(jsDate instanceof Date) || Number.isNaN(jsDate.getTime())) throw new Error('Invalid date');
    const zoned = toZonedTime(jsDate, tz);
    return formatDate(zoned, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: tz });
  } catch (err) {
    return '';
  }
}

export function formatISOStringDisplay(
  isoString, withTime = false, timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
) {
  if (!isoString) return '';

  const dateObj = new Date(isoString);

  // Get the timezone abbreviation
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(dateObj);
  const abbr = parts.find((p) => p.type === 'timeZoneName')?.value || '';

  // Format the date (and time if needed)
  const dateOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone,
  };

  if (withTime) {
    dateOptions.hour = '2-digit';
    dateOptions.minute = '2-digit';
  }

  const dateStr = new Intl.DateTimeFormat(undefined, dateOptions).format(dateObj);

  // Add ordinal suffix to day
  const dayMatch = dateStr.match(/(\d{2})/);
  let dateWithOrdinal = dateStr;
  if (dayMatch) {
    const day = parseInt(dayMatch[1], 10);
    let ordinal = 'th';
    switch (day) {
      case 1:
      case 21:
      case 31:
        ordinal = 'st';
        break;
      case 2:
      case 22:
        ordinal = 'nd';
        break;
      case 3:
      case 23:
        ordinal = 'rd';
        break;
      default:
        ordinal = 'th';
    }

    dateWithOrdinal = dateStr.replace(dayMatch[1], `${day}${ordinal}`);
  }

  return `${dateWithOrdinal} (${abbr})`;
}

export function buildDateTimeLabel(date, shorten = false) {
  // Safety check for invalid date values
  if (!date || typeof date !== 'string' || date === 'up' || date === 'down') {
    return 'N/A';
  }

  const localDate = new Date(date);

  // Check if the date is valid
  if (Number.isNaN(localDate.getTime())) {
    return 'N/A';
  }

  const label = format(localDate, getTimeFormat(date, shorten));
  const tzAbbr = getUserTimezoneAbbr(localDate);
  return `${label} ${tzAbbr}`;
}

export function buildDateLabel(startDate, endDate, shorten = false) {
  try {
    const formattedStartDate = buildDateTimeLabel(startDate, shorten);
    const formattedEndDate = endDate ? buildDateTimeLabel(endDate, shorten) : '—';

    return `${formattedStartDate} - ${formattedEndDate}`;
  } catch (error) {
    // Fallback: return raw values if formatting fails
    const startFallback = startDate || 'N/A';
    const endFallback = endDate || 'N/A';
    return `${startFallback} - ${endFallback}`;
  }
}

export function buildSingleDateLabel(date, shorten = false) {
  try {
    const formattedDate = buildDateTimeLabel(date, shorten);
    return formattedDate;
  } catch (error) {
    // Fallback: return raw value if formatting fails
    return date || 'N/A';
  }
}

export const shortenInt = (n) => {
  if (!n) return '0';

  const number = n.toString();
  const len = number.length;
  const place = len % 3 || 3;
  let abb;

  switch (true) {
    case len > 9:
      abb = 'B';
      break;
    case len > 6:
      abb = 'M';
      break;
    case len > 3:
      abb = 'K';
      break;
    default:
      return number;
  }

  return `${number.slice(0, place)}.${number.slice(place, place + 1)}${abb}`;
};

export const formatUnit = (unit, value) => {
  if (!unit) return '';

  const isSingular = value && (value === 1 || value === '1');
  const displayString = isSingular ? unit : `${unit}s`;

  return displayString;
};

// Simple pluralize helper: if ends with 'y', replace with 'ies', else add 's'
export const pluralize = (word) => (word.endsWith('y') ? `${word.slice(0, -1)}ies` : `${word}s`);

export function formatStripePrice(amount, currency = 'USD', locale = 'en-US') {
  const numericAmount = typeof amount === 'string' ? parseInt(amount, 10) : amount;

  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(numericAmount / 100);

  return formattedPrice;
}

export const secondsSinceEpoch = () => Math.floor(Date.now() / 1000);

export function disableMouseScroll(e) {
  if (e.deltaX || (e.shiftKey && e.deltaY)) {
    e.preventDefault();
    e.stopPropagation();
  }
}

export const isIPadOS = () => (
  window.navigator.platform === 'iPad'
  || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
);

function isHTML(str) {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
}

export const escapedStringParser = (text) => {
  let htmlText = `"${text}"`;
  if (isHTML(text)) htmlText = JSON.stringify(`${text}`);
  return JSON.parse(htmlText);
};

export function openFile(url) {
  const downloadEl = document.createElement('a');
  downloadEl.href = url;
  downloadEl.download = url.substring(url.lastIndexOf('/') + 1);

  document.body.appendChild(downloadEl);

  downloadEl.click();

  setTimeout(() => document.body.removeChild(downloadEl), 500);
}

export function isValidUrl(string) {
  try {
    const validUrl = new URL(string);
    return validUrl && (validUrl.protocol === 'http:' || validUrl.protocol === 'https:');
  } catch (err) {
    return false;
  }
}

// Validate YouTube channel URLs (supports /channel/, /c/, /@, and /user/)
export function isValidYouTubeChannelUrl(url) {
  const YT_CHANNEL_REGEX = /^(?:https?:\/\/)?(www\.|m\.)?youtube\.com\/(channel\/UC[A-Za-z0-9_-]+|c\/[A-Za-z0-9_.-]+|user\/[A-Za-z0-9_.-]+|@[A-Za-z0-9_.-]+)$/;
  return YT_CHANNEL_REGEX.test(url);
}

export function selectEnvironment() {
  switch (window.location.hostname) {
    case 'localhost':
      return 'development';
    case 'beta.redflagai.co':
      return 'beta-1';
    case 'beta-2.redflagai.co':
      return 'beta-2';
    case 'beta-3.redflagai.co':
      return 'beta-3';
    case 'beta-4.redflagai.co':
      return 'beta-4';
    case 'beta-5.redflagai.co':
      return 'beta-5';
    case 'beta-6.redflagai.co':
      return 'beta-6';
    case 'pre-production.redflagai.co':
      return 'pre-production';
    default:
      return 'production';
  }
}

export function baseServerUrl() {
  switch (process.env.NODE_ENV) {
    case 'development':
      return `http://localhost:${process.env.PORT}/`;
    case 'staging':
    case 'production':
      return '/';
    default:
      return '/';
  }
}

export function logCloudwatchError(level, message, stack) {
  if (process.env.NODE_ENV === 'development') return;

  const url = process.env.AWS_ERROR_LOG_URL;

  const meta = {
    agent: navigator.userAgent,
    language: navigator.language,
    level,
    source: 'redflag-auth-app',
    environment: selectEnvironment(),
  };

  const requestBody = JSON.stringify({ meta, message, stack });

  fetch(url, {
    method: 'POST',
    body: requestBody,
  });
}

// Map YouTube policy IDs to action types
const POLICY_IDS = {
  track: 'S395027275175994',
  monetize: 'S650235267251136',
  block: 'S883840562947476',
  routeForReviewToMonetize: 'S417974130152194',
};

// Get action type from match policy, using policyId first, then falling back to rules
export function getMatchPolicyAction(matchPolicy) {
  if (!matchPolicy) return 'track';

  // First, try to determine action from policyId
  if (matchPolicy.policyId) {
    const policyIdToAction = Object.entries(POLICY_IDS).find(
      ([, id]) => id === matchPolicy.policyId,
    );
    if (policyIdToAction) {
      return policyIdToAction[0];
    }
  }

  // Fall back to rules array if policyId doesn't match
  if (matchPolicy.rules && matchPolicy.rules.length > 0 && matchPolicy.rules[0]?.action) {
    return matchPolicy.rules[0].action;
  }

  // Default to 'track' if nothing matches
  return 'track';
}

export function validateReferenceExclusions(value) {
  if (!value || value.trim() === '') return true;
  const intervals = value.split('|').map((i) => i.trim()).filter((i) => i);
  const regex = /^(?:(\d+):)?(\d+):(\d{2})-(?:(\d+):)?(\d+):(\d{2})$/;
  return intervals.every((interval) => {
    if (!regex.test(interval)) return false;
    const [start, end] = interval.split('-');
    const parse = (str) => {
      const parts = str.split(':').map(Number);
      if (parts.length === 2) return parts[0] * 60 + parts[1];
      if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      return NaN;
    };
    if (parse(start) >= parse(end)) return false;
    if (Number(start.split(':').pop()) > 59 || Number(end.split(':').pop()) > 59) return false;
    return true;
  });
}

export const PLATFORM_DISPLAY = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  'facebook-videos': 'Facebook Videos',
  dailymotion: 'Dailymotion',
  'dailymotion-videos': 'Dailymotion Videos',
  'legacy-dailymotion': 'Dailymotion',
  telegram: 'Telegram',
  'telegram-google': 'Telegram',
  'telegram-api': 'Telegram',
  youtube: 'YouTube',
  'youtube-videos': 'YouTube Videos',
  'youtube-google': 'YouTube',
  twitter: 'X',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
  soundcloud: 'SoundCloud',
  linkedin: 'LinkedIn',
  blogspot: 'Blogspot',
  quora: 'Quora',
  tumblr: 'Tumblr',
  disqus: 'Disqus',
  'disqus-forums': 'Disqus',
  'disqus-users': 'Disqus',
  vk: 'VK',
  bing: 'Bing',
  google: 'Google',
  internet: 'Internet',
  'internet-streaming': 'Internet Streaming',
  'internet-download': 'Internet Download',
  'internet-torrent': 'Internet Torrent',
  'search-engine': 'Search Engine',
  shopee: 'Shopee',
  etsy: 'Etsy',
  tokopedia: 'Tokopedia',
  'mercado-libre': 'Mercado Libre',
  ebay: 'eBay',
  amazon: 'Amazon',
  lazada: 'Lazada',
  flipkart: 'Flipkart',
  github: 'GitHub',
  olx: 'OLX',
  vinted: 'Vinted',
  twitch: 'Twitch',
  iptv: 'IPTV',
  kick: 'Kick',
};

export const getStatusDisplay = (status, statusOptions) => {
  if (!status) return null;
  const found = statusOptions?.find((opt) => opt.api === status);
  return found ? found.display : status;
};

export function validateOwnership(value) {
  if (!value || value.trim() === '') return true; // Empty means worldwide
  // Allowed: pipes, spaces, WW, -country, percentage, country codes
  const ownershipRegex = /^(\d{1,3}%\s*)?(WW|(-)?([A-Z]{2}(?:\s+[A-Z]{2})*)?)(\|(\d{1,3}%\s*)?(WW|(-)?([A-Z]{2}(?:\s+[A-Z]{2})*)?))*$/;
  return ownershipRegex.test(value.trim());
}

const ASSET_TYPE_OPTIONS = [
  'web', 'music video', 'sound recording', 'art track', 'movie', 'episode', 'composition',
];

const MATCH_POLICY_OPTIONS = [
  'monetize', 'track', 'block', 'routeforreviewtomonetize',
];

export function validateCsvRows(rows, headers) {
  const required = headers.filter((h) => h.required).map((h) => h.key);
  const assetTypeKey = headers.find((h) => h.key.toLowerCase().includes('asset type'))?.key;
  const matchPolicyKey = headers.find((h) => h.key.toLowerCase().includes('match policy'))?.key;
  const ownershipKey = headers.find((h) => h.key.toLowerCase().includes('ownership'))?.key;
  const assetTitleKey = headers.find((h) => h.key.toLowerCase().includes('asset title'))?.key;
  const assetDescKey = headers.find((h) => h.key.toLowerCase().includes('asset description'))?.key;
  const notesKey = headers.find((h) => h.key.toLowerCase().includes('notes'))?.key;

  const errors = [];

  rows.forEach((row, i) => {
    // Required fields
    required.forEach((req) => {
      if (!row[req] || row[req].trim() === '') {
        errors.push(`Row ${i + 2}: Missing required field "${req}"`);
      }
    });
    // Asset Title max 255
    if (assetTitleKey && row[assetTitleKey] && row[assetTitleKey].length > 255) {
      errors.push(`Row ${i + 2}: Asset Title exceeds 255 characters`);
    }
    // Asset Description max 5000
    if (assetDescKey && row[assetDescKey] && row[assetDescKey].length > 5000) {
      errors.push(`Row ${i + 2}: Asset Description exceeds 5000 characters`);
    }
    // Notes max 5000
    if (notesKey && row[notesKey] && row[notesKey].length > 5000) {
      errors.push(`Row ${i + 2}: Notes exceeds 5000 characters`);
    }
    // Asset Type options
    if (assetTypeKey && row[assetTypeKey]) {
      const type = row[assetTypeKey].toLowerCase().trim();
      if (!ASSET_TYPE_OPTIONS.includes(type)) {
        errors.push(`Row ${i + 2}: Invalid Asset Type "${row[assetTypeKey]}"`);
      }
    }
    // Match Policy options
    if (matchPolicyKey && row[matchPolicyKey]) {
      const policy = row[matchPolicyKey].toLowerCase().trim();
      if (!MATCH_POLICY_OPTIONS.includes(policy)) {
        errors.push(`Row ${i + 2}: Invalid Match Policy "${row[matchPolicyKey]}"`);
      }
    }
    // Ownership format
    if (ownershipKey && row[ownershipKey] && !validateOwnership(row[ownershipKey])) {
      errors.push(`Row ${i + 2}: Invalid Ownership format`);
    }
    // Reference Exclusions format
    if (row['Reference Exclusions'] && !validateReferenceExclusions(row['Reference Exclusions'])) {
      errors.push(`Row ${i + 2}: Invalid Reference Exclusions format`);
    }
  });

  if (errors.length > 0) {
    return { valid: false, errors };
  }
  return { valid: true };
}
